import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { Button } from "../../../components/forms/Buttons/Button";
import Input from "../../../components/forms/Input/Input";
import Select from "../../../components/forms/Select/Select";
import { sendMailByRole } from "../../../services/auth/emailService";
import { toast } from "react-toastify";

export default function SendMail() {
  const [t] = useTranslation();
  const [loading, setLoading] = useState(false);
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      role: null,
      subject: "",
      message: "",
    },
  });

  const roleOptions = [
    { label: t("admin.send_mail.role.participant"), value: "PARTICIPANT" },
    { label: t("admin.send_mail.role.supervisor"), value: "SUPERVISOR" },
  ];

  const onSubmit = (data) => {
    setLoading(true);
    const emailData = {
      role: data.role,
      subject: data.subject,
      message: data.message,
    };

    sendMailByRole(emailData)
      .then((response) => {
        toast.success(t("admin.send_mail.success"));
        reset();
      })
      .catch((err) => {
        const errorMessage =
          err.response?.data?.error ||
          err.response?.data?.detail ||
          t("admin.send_mail.error");
        toast.error(errorMessage);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg border border-gray-300 p-8 max-w-2xl mx-auto">
        <h2 className="text-xl font-semibold mb-4">
          {t("admin.send_mail.title")}
        </h2>
        <p className="text-sm text-gray-600 mb-6">
          {t("admin.send_mail.description")}
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <Controller
            name="role"
            control={control}
            rules={{ required: t("admin.send_mail.role_required") }}
            render={({ field }) => (
              <Select
                label={t("admin.send_mail.role_label")}
                placeholder={t("admin.send_mail.select_role")}
                options={roleOptions}
                value={field.value}
                onChange={(option) => field.onChange(option?.value)}
                errorMsg={errors?.role?.message}
                required
                className="w-full text-sm"
              />
            )}
          />

          <Input
            label={t("admin.send_mail.subject_label")}
            placeholder={t("admin.send_mail.subject_placeholder")}
            errorMsg={errors?.subject?.message}
            inputClassName={`bg-white shadow-none outline-1 outline-gray-300 md:rounded-lg h-10 ${
              errors?.subject?.message && "ring-error ring-1"
            }`}
            className="w-full text-sm"
            type="text"
            required
            {...register("subject", {
              required: t("admin.send_mail.subject_required"),
            })}
          />

          <div className="w-full text-sm">
            <label className="block text-sm font-medium text-gray-700">
              {t("admin.send_mail.message_label")}
              <span className="text-error">*</span>
            </label>
            <textarea
              className={`block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm sm:text-sm mt-1 ${
                errors?.message?.message && "ring-error ring-1"
              }`}
              rows={8}
              placeholder={t("admin.send_mail.message_placeholder")}
              {...register("message", {
                required: t("admin.send_mail.message_required"),
              })}
            />
            {errors?.message?.message && (
              <span className="mt-1 text-xs font-semibold text-error">
                {errors.message.message}
              </span>
            )}
            <p className="text-xs text-gray-500 mt-2">
              {t("admin.send_mail.placeholders_info")}
            </p>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button
              text={t("buttons.cancel")}
              onClick={() => reset()}
              disabled={loading}
            />
            <Button
              text={t("admin.send_mail.send_button")}
              type="submit"
              color="primary"
              disabled={loading}
            />
          </div>
        </form>
      </div>
    </div>
  );
}
